import { OrgAdminApiError, OrgAdminUnauthorizedError } from '@/api/orgAdmin';
import type { Paginated } from '@/api/console';
import { useSessionStore } from '@/store/sessionStore';
import type { KeycloakAccountRow } from './types';

/**
 * EAA-1820: fetch module for `GET /api/v1/accounts` (EAA-1829) — the read
 * side of the console accounts screen. `@/api/console.ts` already ships
 * `issueAccount` / `resetAccountPassword` / `deleteAccount` (the write side);
 * this file adds ONLY the list read, which did not exist before EAA-1829 and
 * which this task's brief says must live outside `src/api/*` (owned by
 * another agent). It should migrate into `@/api/console.ts` alongside its
 * siblings once that ownership boundary lifts — see the page-level report.
 *
 * Mirrors `@/api/console.ts`'s own `request()` byte-for-byte (envelope
 * unwrap, 401 → `OrgAdminUnauthorizedError`, `X-Org-Id` passthrough) rather
 * than importing it, since that helper is not exported.
 */

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    data: T;
}

async function request<T>(apiBase: string, token: string, path: string): Promise<T> {
    const orgId = useSessionStore.getState().actingOrgId;
    const res = await fetch(`${apiBase}${path}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            ...(orgId ? { 'X-Org-Id': orgId } : {}),
        },
    });
    if (res.status === 401) {
        throw new OrgAdminUnauthorizedError();
    }
    const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
    if (!res.ok || !envelope?.success) {
        throw new OrgAdminApiError(
            envelope?.description ?? `Request failed (${res.status})`,
            envelope?.code ?? res.status,
            (envelope as { data?: unknown } | null)?.data
        );
    }
    return envelope.data;
}

export interface FetchAccountsParams {
    page?: number;
    page_size?: number;
    search?: string;
    /** EAA-1820: real server-side filters `AccountsView.get` now accepts — see
     *  `useRealmAccountsAdapter.ts` for how the shared screen's facets/status
     *  chips map onto these. */
    status?: 'active' | 'pending' | 'locked';
    permission?: 'granted' | 'none';
    organization_id?: string;
    source?: 'console' | 'unknown';
    /** `username` | `email` | `created` | `status`, optionally `-` prefixed. */
    ordering?: string;
}

/**
 * EAA-1820: `GET /api/v1/accounts/facets` — the realm-wide facet counts that
 * replaced `useAccountsScan`'s capped client-side walk. `saved_filters` backs
 * the four constant "Bộ lọc đã lưu" chips (`no_permission` → `permission=none`,
 * `never_logged_in` → `status=pending` — see this endpoint's own `account_status`
 * logic, which computes both from the same `required_actions` check, so the two
 * numbers are never inconsistent with each other).
 */
export interface AccountsFacets {
    total: number;
    status: { active: number; pending: number; locked: number };
    permission: { granted: number; none: number };
    source: { console: number; unknown: number };
    organizations: { organization_id: string; organization_name: string; count: number }[];
    saved_filters: { all: number; no_permission: number; never_logged_in: number; locked: number };
    /** True when the facet counts themselves are a prefix, not the whole realm —
     *  see `AccountsPage`'s docstring for how this is surfaced honestly. */
    capped: boolean;
}

export function fetchAccountFacets(
    apiBase: string,
    token: string,
    search?: string
): Promise<AccountsFacets> {
    const qs = new URLSearchParams();
    if (search?.trim()) qs.set('search', search.trim());
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<AccountsFacets>(apiBase, token, `/api/v1/accounts/facets${suffix}`);
}

/**
 * `GET /api/v1/accounts` — paginated Keycloak users read (EAA-1829). Gated
 * server-side to `IsSuperAdmin`; this is realm-wide, NOT org-scoped — see
 * `account_views.py::AccountsView.get`. Never returns a credential field.
 */
/**
 * EAA-1820: `GET /accounts` returns two fields beyond `Paginated<T>` that
 * describe the last-login column as a whole rather than any one row.
 * `last_login_available: false` means the realm records no login events, so
 * every row's null `last_login` is UNKNOWN — not "never signed in".
 */
export interface AccountsPage extends Paginated<KeycloakAccountRow> {
    last_login_available?: boolean;
    last_login_window_days?: number;
    /** Mirrors `AccountsFacets.capped` — true when the count itself is a
     *  prefix, not the whole realm. */
    capped?: boolean;
}

/**
 * EAA-1820 (frame 67): `GET /api/v1/accounts/<sub>` — the account profile.
 *
 * Distinct from a list row on purpose: EVERY active placement (a person can sit
 * in several đơn vị) and roles GROUPED BY APPLICATION, which a table cell has no
 * room for and which flat keys cannot express — two apps sharing a role name
 * would be indistinguishable.
 */
export interface AccountPlacement {
    organization_id: string;
    organization_name: string;
    level_name: string;
    role_key: string;
}

export interface AccountAppAccess {
    application: string;
    application_name: string;
    roles: string[];
}

export interface AccountDetail extends KeycloakAccountRow {
    first_name?: string;
    last_name?: string;
    placements: AccountPlacement[];
    app_access: AccountAppAccess[];
    last_login_available?: boolean;
}

export function fetchAccount(
    apiBase: string,
    token: string,
    sub: string
): Promise<AccountDetail> {
    return request<AccountDetail>(apiBase, token, `/api/v1/accounts/${encodeURIComponent(sub)}`);
}

export function fetchAccounts(
    apiBase: string,
    token: string,
    params: FetchAccountsParams = {}
): Promise<AccountsPage> {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page ?? 1));
    qs.set('page_size', String(params.page_size ?? 100));
    if (params.search?.trim()) {
        qs.set('search', params.search.trim());
    }
    if (params.status) qs.set('status', params.status);
    if (params.permission) qs.set('permission', params.permission);
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.source) qs.set('source', params.source);
    if (params.ordering) qs.set('ordering', params.ordering);
    return request<AccountsPage>(apiBase, token, `/api/v1/accounts?${qs.toString()}`);
}
